import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EntityFormDialog } from "@/components/EntityFormDialog";
import { IntestazioniSettings } from "@/components/IntestazioniSettings";
import { GraficiSettings, CategorieSettings, CommercialistaSettings } from "@/components/StatisticheSettings";
import { LavoriFissiSettings, SpeseSettings } from "@/components/ElenchiSettings";
import { Checkbox } from "@/components/ui/checkbox";

const NEG = [
  ["nome_negozio", "Nome negozio"], ["indirizzo", "Indirizzo"], ["telefono", "Telefono"],
  ["email", "Email"], ["partita_iva", "Partita IVA"], ["tariffa_oraria", "Tariffa oraria manodopera (€)"],
];
const STAFF_FIELDS = [
  { name: "name", label: "Nome", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "password", label: "Password (min 6 caratteri)", type: "password", required: true },
  { name: "role", label: "Ruolo", type: "select", default: "staff", options: [{ value: "staff", label: "Staff" }, { value: "admin", label: "Amministratore" }] },
];

const Staff = ({ me }) => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const permesso = async (u, v) => {
    try { await api.patch(`/users/${u.id}/permessi`, { vede_statistiche: v }); load(); toast.success(v ? `${u.name} ora vede grafici e guadagni` : `${u.name} non vede più grafici e guadagni`); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="staff-section">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h2 className="font-heading text-base font-bold">Account staff</h2>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="staff-new-button"><Plus className="mr-1 h-3.5 w-3.5" /> Nuovo account</Button>
      </div>
      <ul className="divide-y divide-slate-100">
        {users.map((u) => (
          <li key={u.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`staff-row-${u.id}`}>
            <div className="min-w-0 flex-1"><p className="font-semibold">{u.name}</p><p className="text-xs text-slate-500">{u.email} · {u.role === "admin" ? "Amministratore" : "Staff"}</p>
              {u.role === "admin" ? <p className="mt-1 text-[11px] text-slate-400">Vede sempre grafici, guadagni e spese</p> : (
                <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={Boolean(u.vede_statistiche)} onCheckedChange={(v) => permesso(u, Boolean(v))} data-testid={`staff-statistiche-${u.id}`} />Può vedere grafici, guadagni e spese</label>
              )}
            </div>
            {u.id !== me.id && <ConfirmDelete testId={`staff-delete-${u.id}`} onConfirm={() => remove(u.id)} />}
          </li>
        ))}
      </ul>
      <EntityFormDialog open={open} onOpenChange={setOpen} endpoint="users" fields={STAFF_FIELDS} title="Nuovo account staff" prefix="staff-form" onSaved={load} />
    </section>
  );
};

export default function Impostazioni() {
  const { user, ricarica } = useAuth();
  const isAdmin = user?.role === "admin";
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/impostazioni").then((r) => setF(r.data)).catch((e) => toast.error(formatError(e))); }, []);

  const save = async (e) => {
    e?.preventDefault();
    try {
      await api.put("/impostazioni", { ...f, tariffa_oraria: Number(f.tariffa_oraria) || 0 });
      toast.success("Impostazioni salvate");
      ricarica();
      return true;
    } catch (err) {
      toast.error(formatError(err));
      return false;
    }
  };

  if (!f) return <div className="py-20 text-center text-sm text-slate-500" data-testid="impostazioni-loading">Caricamento...</div>;
  return (
    <div>
      <PageHeader overline="Configurazione" title="Impostazioni" />
      <div className="grid gap-6 xl:grid-cols-2">
        <form onSubmit={save} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="negozio-form">
          <h2 className="mb-1 font-heading text-base font-bold">Dati negozio</h2>
          <p className="mb-4 text-xs text-slate-500">Compaiono nell'intestazione dei documenti stampati.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {NEG.map(([k, label]) => (
              <div key={k}>
                <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>
                <Input disabled={!isAdmin} type={k === "tariffa_oraria" ? "number" : "text"} value={f[k] ?? ""} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`negozio-${k.replace(/_/g, "-")}-input`} />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Condizioni del contratto di noleggio</Label>
            <Textarea disabled={!isAdmin} rows={7} value={f.condizioni_noleggio ?? ""} onChange={(e) => setF((s) => ({ ...s, condizioni_noleggio: e.target.value }))} data-testid="negozio-condizioni-noleggio-input" />
            <p className="mt-1 text-[11px] text-slate-400">Testo stampato nel contratto di noleggio. Verificalo con il tuo consulente.</p>
          </div>
          <div className="mt-4">
            <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Informativa privacy (GDPR)</Label>
            <Textarea disabled={!isAdmin} rows={9} value={f.informativa_privacy ?? ""} onChange={(e) => setF((s) => ({ ...s, informativa_privacy: e.target.value }))} data-testid="negozio-informativa-privacy-input" />
            <p className="mt-1 text-[11px] text-slate-400">Testo stampato nel modulo privacy che il cliente firma. Verificalo con il tuo consulente.</p>
          </div>
          {isAdmin && <Button type="submit" className="mt-5 bg-orange-600 hover:bg-orange-700" data-testid="negozio-save-button">Salva</Button>}
        </form>
        <div className="space-y-6">
          <IntestazioniSettings f={f} setF={setF} isAdmin={isAdmin} onSave={save} />
          {isAdmin && <Staff me={user} />}
          <GraficiSettings f={f} setF={setF} isAdmin={isAdmin} onSave={save} />
          {isAdmin && <CommercialistaSettings f={f} setF={setF} isAdmin={isAdmin} onSave={save} />}
        </div>
        <CategorieSettings f={f} setF={setF} isAdmin={isAdmin} onSave={save} />
        <LavoriFissiSettings />
        {user?.puo_statistiche && <div className="xl:col-span-2"><SpeseSettings /></div>}
      </div>
    </div>
  );
}
